import { useState, useEffect } from 'react';
import {
  Server, Cpu, Plus, Trash2, Edit3, X, RotateCw, RefreshCw, Copy
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import AiBotWidget from '../components/ai-bot/AiBotWidget';

// ─── Types ──────────────────────────────────────────

type ApiProtocol = 'REST' | 'RPC' | 'SOAP';
type ApiDirection = 'INBOUND' | 'OUTBOUND';
type ApiAuthType = 'NONE' | 'IP_WHITELIST' | 'USER_PASSWORD' | 'SSH_KEY' | 'BEARER_TOKEN' | 'ACCESS_KEY' | 'CERTIFICATE';
type ApiDataFormat = 'JSON' | 'XML';
type UrlSourceType = 'external' | 'system';

interface ApiEndpoint {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  direction: ApiDirection;
  protocol: ApiProtocol;
  dataFormat: ApiDataFormat;
  endpointUrl?: string;
  authType: ApiAuthType;
  authConfig?: string;
  authCombinator?: string;
  paramsMethod?: string;
  isActive: boolean;
  fieldMappings?: ApiFieldMapping[];
  createdAt: string;
}

interface ApiFieldMapping {
  id: string;
  dataFieldId: string;
  sourceKey?: string;
  targetKey?: string;
  transformFn?: string;
  isExport: boolean;
  splitArray?: boolean;
  itemTimestampKey?: string;
  createEntry?: boolean;
  dataField?: { id: string; name: string; type: string };
}

interface DataField {
  id: string;
  name: string;
  type: string;
  hasPersonReference?: boolean;
  isSensitive?: boolean;
}

// ─── Form Defaults ─────────────────────────────────

const EMPTY_ENDPOINT = {
  name: '', description: '', direction: 'INBOUND' as ApiDirection,
  protocol: 'REST' as ApiProtocol, dataFormat: 'JSON' as ApiDataFormat,
  endpointUrl: '', authType: 'BEARER_TOKEN' as ApiAuthType,
  authConfig: '', authCombinator: 'AND', paramsMethod: 'GET', scheduleCron: '', urlTemplate: '', isActive: true,
};

// ─── Helpers ────────────────────────────────────────

const labelClass = 'text-sm text-gray-700 block mb-1';
const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-colors';

const AUTH_LABELS: Record<ApiAuthType, string> = {
  NONE: 'Keine Authentifizierung',
  IP_WHITELIST: 'IP-Whitelist', USER_PASSWORD: 'Benutzer/Passwort',
  SSH_KEY: 'SSH-Key', BEARER_TOKEN: 'Bearer-Token',
  ACCESS_KEY: 'Access-Key', CERTIFICATE: 'Zertifikat',
};

const authConfigPlaceholder: Record<ApiAuthType, string> = {
  NONE: '',
  IP_WHITELIST: '10.0.0.0/8, 192.168.1.0/24',
  USER_PASSWORD: '{"username":"...","password":"..."}',
  SSH_KEY: '{"privateKey":"-----BEGIN...","passphrase":"..."}',
  BEARER_TOKEN: '{"token":"..."}',
  ACCESS_KEY: '{"accessKey":"...","secretKey":"..."}',
  CERTIFICATE: '{"cert":"-----BEGIN CERT...","key":"-----BEGIN..."}',
};

function getSystemUrl(endpointId: string, direction: ApiDirection): string {
  const base = 'https://datensammlung.mkubalek.eu/api/v1/api-endpoints';
  return direction === 'INBOUND'
    ? `${base}/${endpointId}/receive`
    : `${base}/${endpointId}/query`;
}

// ─── Component ─────────────────────────────────────

export default function ApiEndpointsPage() {
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const isGsAdmin = user?.role === "ADMIN_GENERALSEKRETARIAT";

  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
  const [fields, setFields] = useState<DataField[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({ ...EMPTY_ENDPOINT });
  const [urlSource, setUrlSource] = useState<UrlSourceType>('external');
  const [testResult, setTestResult] = useState<string | null>(null);

  // Mapping editor state
  const [showMapping, setShowMapping] = useState<string | null>(null);
  const [newMapping, setNewMapping] = useState<Record<string, any>>({});
  const [previewFields, setPreviewFields] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});
  const [showFieldPicker, setShowFieldPicker] = useState<Record<string, boolean>>({});

  // Tab state
  const [activeTab, setActiveTab] = useState<'endpoints' | 'leafs'>('endpoints');

  useEffect(() => {
    if (!orgId) return;
    loadData();
  }, [orgId]);

  async function loadData(options?: { silent?: boolean }) {
    if (!options?.silent) setLoading(true);
    try {
      const [epRes, fieldRes] = await Promise.all([
        api.get(`/api-endpoints?organizationId=${orgId}`),
        api.get(`/data-sources/fields?organizationId=${orgId}`),
      ]);
      setEndpoints(epRes.data || []);
      setFields(fieldRes.data || []);
    } catch { /* ignore */ }
    if (!options?.silent) setLoading(false);
  }

  // ─── CRUD Actions ─────────────────────────────────

  function openCreate() {
    setForm({ ...EMPTY_ENDPOINT, organizationId: orgId });
    setUrlSource('external');
    setEditing(null);
    setShowForm(true);
    setTestResult(null);
  }

  /**
   * Der KI-Bot hat den Endpunkt bereits angelegt. Formular schließen und
   * zurücksetzen, damit die Anlage nicht versehentlich erneut abgeschickt
   * wird; die Liste wird sofort neu geladen.
   */
  function handleBotCreated() {
    setShowForm(false);
    setEditing(null);
    setForm({ ...EMPTY_ENDPOINT });
    setUrlSource('external');
    setTestResult(null);
    // Silent, damit die Ladeansicht das Chat-Panel (mit der Rückmeldung
    // „wurde angelegt“) nicht unmountet.
    loadData({ silent: true });
  }

  function openEdit(ep: ApiEndpoint) {
    setForm({
      name: ep.name,
      description: ep.description || '',
      direction: ep.direction,
      protocol: ep.protocol,
      dataFormat: ep.dataFormat,
      endpointUrl: ep.endpointUrl || '',
      authType: ep.authType,
      authConfig: ep.authConfig || '',
      authCombinator: ep.authCombinator || 'AND',
      paramsMethod: ep.paramsMethod || 'GET',
      scheduleCron: (ep as any).scheduleCron || '',
      urlTemplate: (ep as any).urlTemplate || '',
      isActive: ep.isActive,
    });
    setUrlSource(ep.endpointUrl ? 'external' : 'system');
    setEditing(ep.id);
    setShowForm(true);
    setTestResult(null);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = { ...form, organizationId: orgId };

    // Wenn System-URL gewählt: endpointUrl leeren (wird vom System verwaltet)
    if (urlSource === 'system') {
      payload.endpointUrl = '';
      payload.urlTemplate = '';
      payload.scheduleCron = '';
    }

    try {
      if (editing) {
        await api.patch(`/api-endpoints/${editing}`, payload);
      } else {
        await api.post('/api-endpoints', payload);
      }
      setShowForm(false);
      setEditing(null);
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('API-Endpunkt wirklich löschen?')) return;
    await api.delete(`/api-endpoints/${id}`);
    loadData();
  }

  async function testEndpoint() {
    if (!form.endpointUrl) { setTestResult('❌ Keine URL angegeben'); return; }
    try {
      new URL(form.endpointUrl);
      const start = Date.now();
      await fetch(form.endpointUrl, { method: 'HEAD', mode: 'no-cors' }).catch(() => {});
      setTestResult(`✅ URL-Format OK (${Date.now() - start}ms, nicht angetestet — CORS-beschränkt)`);
    } catch {
      setTestResult('❌ Ungültige URL');
    }
  }

  // ─── Mapping Actions ──────────────────────────────

  async function fetchApiFields(endpointId: string) {
    setPreviewLoading(prev => ({ ...prev, [endpointId]: true }));
    try {
      const res = await api.get(`/api-endpoints/${endpointId}/preview-fields`);
      setPreviewFields(res.data?.fields || []);
      setShowFieldPicker(prev => ({ ...prev, [endpointId]: true }));
    } catch (err: any) {
      console.error('Failed to fetch API fields:', err);
    } finally {
      setPreviewLoading(prev => ({ ...prev, [endpointId]: false }));
    }
  }

  async function addMapping(endpointId: string) {
    const ep = endpoints.find(e => e.id === endpointId);
    if (!newMapping.dataFieldId) return;
    if (ep?.direction === 'OUTBOUND' && !newMapping.targetKey) return;
    if (ep?.direction !== 'OUTBOUND' && !newMapping.sourceKey) return;
    try {
      await api.post(`/api-endpoints/${endpointId}/field-mappings`, {
        ...newMapping,
        // Eintrag pro Element ist nur wirksam, wenn das Array aufgeteilt wird
        createEntry: newMapping.splitArray === true && newMapping.createEntry === true,
      });
      setNewMapping({});
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  // ─── Execute / Refresh ────────────────────────────

  const [execResult, setExecResult] = useState<{ [key: string]: any }>({});

  async function executeNow(endpointId: string, direction: string) {
    setExecResult({});
    try {
      // OUTBOUND: /execute, INBOUND mit externer URL: /refresh
      const endpoint = direction === 'INBOUND' ? 'refresh' : 'execute';
      const res = await api.post(`/api-endpoints/${endpointId}/${endpoint}`, {});
      setExecResult({ [endpointId]: res.data });
    } catch (err: any) {
      setExecResult({ [endpointId]: { error: err.response?.data?.message || err.message } });
    }
  }

  async function removeMapping(mappingId: string) {
    try {
      await api.delete(`/api-endpoints/field-mappings/${mappingId}`);
      loadData();
    } catch (err: any) {
      alert(`Fehler: ${err.response?.data?.message || err.message}`);
    }
  }

  if (loading) return <div className="p-8 text-center text-gray-400">Lade...</div>;

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('endpoints')}
          className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'endpoints'
              ? 'bg-white text-primary border border-b-white border-gray-200 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Cpu size={16} className="inline mr-1.5" />API-Endpunkte
        </button>
        {isGsAdmin && (
          <button
            onClick={() => setActiveTab('leafs')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'leafs'
                ? 'bg-white text-primary border border-b-white border-gray-200 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Server size={16} className="inline mr-1.5" />Verbundene Systeme
          </button>
        )}
      </div>

      {activeTab === 'endpoints' ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cpu size={28} className="text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">API-Endpunkte</h1>
                <p className="text-sm text-gray-500">Ein- und ausgehende Schnittstellen verwalten</p>
              </div>
            </div>
            <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus size={16} /> Endpunkt
            </button>
          </div>

{/* ─── Form Modal ──────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{editing ? 'API-Endpunkt bearbeiten' : 'Neuer API-Endpunkt'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>Name *</label>
                  <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="z.B. LV Tirol Schnittstelle" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Beschreibung</label>
                  <input className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
                </div>
                <div>
                  <label className={labelClass}>Richtung</label>
                  <select className={inputClass} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                    <option value="INBOUND">Eingehend (INBOUND)</option>
                    <option value="OUTBOUND">Ausgehend (OUTBOUND)</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Protokoll</label>
                  <select className={inputClass} value={form.protocol} onChange={(e) => setForm({ ...form, protocol: e.target.value })}>
                    <option value="REST">REST</option>
                    <option value="RPC">RPC</option>
                    <option value="SOAP">SOAP</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Datenformat</label>
                  <select className={inputClass} value={form.dataFormat} onChange={(e) => setForm({ ...form, dataFormat: e.target.value })}>
                    <option value="JSON">JSON</option>
                    <option value="XML">XML</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Parameter-Methode</label>
                  <select className={inputClass} value={form.paramsMethod} onChange={(e) => setForm({ ...form, paramsMethod: e.target.value })}>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>

                {/* ═══ URL-Quelle auswählen ═══ */}
                <div className="md:col-span-2 border-t border-gray-200 pt-4">
                  <label className={labelClass}>URL-Quelle</label>
                  <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="urlSource"
                        checked={urlSource === 'external'}
                        onChange={() => setUrlSource('external')}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="text-gray-700">🔗 Externe URL <span className="text-gray-400 font-normal">(System ruft Daten ab/sendet)</span></span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="radio"
                        name="urlSource"
                        checked={urlSource === 'system'}
                        onChange={() => setUrlSource('system')}
                        className="h-4 w-4 text-primary focus:ring-primary"
                      />
                      <span className="text-gray-700">🖥️ System-URL <span className="text-gray-400 font-normal">(andere Systeme verbinden sich)</span></span>
                    </label>
                  </div>
                </div>

                {/* ═══ Externe URL-Felder ═══ */}
                {urlSource === 'external' && (
                  <div className="md:col-span-2 border-t border-gray-200 pt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      🔗 Externe URL <span className="font-normal normal-case text-gray-400">(Daten {form.direction === 'INBOUND' ? 'abrufen' : 'senden'})</span>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className={labelClass}>
                          URL-Template <span className="text-xs text-gray-400">(mit {'{param}'} Platzhaltern)</span>
                        </label>
                        <input
                          className={`${inputClass} font-mono text-xs`}
                          value={form.urlTemplate}
                          onChange={(e) => setForm({ ...form, urlTemplate: e.target.value })}
                          placeholder="https://api.behoerde.at/v1/{jahr}/{org_id}/daten"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>Cron-Ausdruck <span className="text-xs text-gray-400">(für periodischen Abruf/Ausführung)</span></label>
                        <input
                          className={`${inputClass} font-mono text-xs`}
                          value={form.scheduleCron}
                          onChange={(e) => setForm({ ...form, scheduleCron: e.target.value })}
                          placeholder="z.B. 0 */6 * * * (alle 6 Stunden)"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>Endpoint-URL</label>
                        <div className="flex gap-2">
                          <input
                            className={`${inputClass} flex-1`}
                            value={form.endpointUrl}
                            onChange={(e) => setForm({ ...form, endpointUrl: e.target.value })}
                            placeholder="https://api.externes-system.at/v1/data"
                          />
                          <button type="button" onClick={testEndpoint} className="btn-secondary text-xs px-2 flex items-center gap-1"><RotateCw size={12} />Test</button>
                        </div>
                        {testResult && <p className={`text-xs mt-1 ${testResult.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>{testResult}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══ System-generierte URL ═══ */}
                {urlSource === 'system' && (
                  <div className="md:col-span-2 border-t border-gray-200 pt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      🖥️ System-generierte URL <span className="font-normal normal-case text-gray-400">(Daten {form.direction === 'INBOUND' ? 'empfangen' : 'bereitstellen'})</span>
                    </p>
                    <div className="flex gap-2">
                      <input
                        className={`${inputClass} flex-1 bg-gray-50 text-gray-500 font-mono text-xs`}
                        value={editing
                          ? getSystemUrl(editing, form.direction)
                          : '(wird nach dem Speichern generiert)'}
                        readOnly
                        disabled
                      />
                      {editing && (
                        <button type="button" onClick={() => navigator.clipboard.writeText(getSystemUrl(editing, form.direction))} className="btn-secondary text-xs px-2" title="Kopieren">
                          📋
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {form.direction === 'INBOUND'
                        ? 'Externe Systeme können Daten an diese URL senden (POST, Webhook).'
                        : 'Externe Systeme können Daten von dieser URL abrufen (GET, API-Query).'}
                    </p>
                    {!editing && (
                      <p className="text-xs text-amber-600 mt-1">💡 URL wird nach dem Erstellen sichtbar.</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Auth-Typ</label>
                  <select className={inputClass} value={form.authType} onChange={(e) => setForm({ ...form, authType: e.target.value })}>
                    {Object.entries(AUTH_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Auth-Kombinator</label>
                  <select className={inputClass} value={form.authCombinator} onChange={(e) => setForm({ ...form, authCombinator: e.target.value })}>
                    <option value="AND">UND (AND)</option>
                    <option value="OR">ODER (OR)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Auth-Konfiguration (JSON)</label>
                  <textarea className={`${inputClass} font-mono text-xs h-20`} value={form.authConfig} onChange={(e) => setForm({ ...form, authConfig: e.target.value })} placeholder={authConfigPlaceholder[form.authType as ApiAuthType]} />
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                    <span className="text-sm text-gray-700">Aktiv</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Abbrechen</button>
                <button type="submit" className="btn-primary text-sm">{editing ? 'Speichern' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ Endpoint List ═══ */}
      {endpoints.length === 0 ? (
        <div className="card text-center py-12">
          <Cpu size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Keine API-Endpunkte konfiguriert.</p>
          <p className="text-xs text-gray-400 mt-1">Erstelle einen Endpunkt für REST, SOAP oder RPC-Schnittstellen.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {endpoints.map((ep) => {
            const hasExternalUrl = !!ep.endpointUrl;
            const hasSystemUrl = !hasExternalUrl;
            return (
            <div key={ep.id} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${ep.direction === 'INBOUND' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                    {ep.direction === 'INBOUND' ? '→' : '←'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{ep.name}</h3>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{ep.protocol}</span>
                      <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">{ep.dataFormat}</span>
                      {ep.isActive ? <span className="text-xs text-green-600">● Aktiv</span> : <span className="text-xs text-gray-400">○ Inaktiv</span>}
                      {!hasExternalUrl && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">System-URL</span>}
                    </div>
                    {ep.description && <p className="text-xs text-gray-500 mt-0.5">{ep.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span className="font-mono">{AUTH_LABELS[ep.authType]}</span>
                      {hasExternalUrl && <span className="font-mono truncate max-w-xs">{ep.endpointUrl}</span>}
                      {(ep as any).scheduleCron && <span className="font-mono text-purple-500">Cron: {(ep as any).scheduleCron}</span>}
                      {hasSystemUrl && <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(getSystemUrl(ep.id, ep.direction)); }} className="font-mono text-blue-500 hover:text-blue-700 hover:underline cursor-pointer bg-transparent border-none text-xs p-0" title="URL kopieren">{getSystemUrl(ep.id, ep.direction)}</button>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  {/* OUTBOUND → Jetzt ausführen */}
                  {ep.direction === 'OUTBOUND' && (
                    <button onClick={(e) => { e.stopPropagation(); executeNow(ep.id, 'OUTBOUND'); }} className="p-1.5 text-gray-400 hover:text-green-600 transition-colors" title="Jetzt ausführen"><RotateCw size={15} /></button>
                  )}
                  {/* INBOUND mit externer URL → Jetzt abrufen */}
                  {ep.direction === 'INBOUND' && hasExternalUrl && (
                    <button onClick={(e) => { e.stopPropagation(); executeNow(ep.id, 'INBOUND'); }} className="p-1.5 text-gray-400 hover:text-green-600 transition-colors" title="Daten jetzt abrufen"><RefreshCw size={15} /></button>
                  )}
                  {/* INBOUND mit System-URL → Kopieren */}
                  {ep.direction === 'INBOUND' && hasSystemUrl && (
                    <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(getSystemUrl(ep.id, ep.direction)); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title="System-URL kopieren"><Copy size={15} /></button>
                  )}
                  <button onClick={() => openEdit(ep)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title="Bearbeiten"><Edit3 size={15} /></button>
                  <button onClick={() => handleDelete(ep.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" title="Löschen"><Trash2 size={15} /></button>
                </div>
              </div>

              {/* Execution / Refresh Result */}
              {execResult[ep.id] && (
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-blue-700">
                      {ep.direction === 'INBOUND' ? 'Abrufergebnis' : 'Ausführungsergebnis'}
                    </span>
                    <button onClick={() => { const r = { ...execResult }; delete r[ep.id]; setExecResult(r); }} className="text-blue-400 hover:text-blue-600"><X size={12} /></button>
                  </div>
                  <pre className="mt-1 font-mono text-xs text-blue-800 whitespace-pre-wrap overflow-x-auto max-h-32">
                    {JSON.stringify(execResult[ep.id], null, 2)}
                  </pre>
                </div>
              )}

              {/* Field Mappings */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setShowMapping(showMapping === ep.id ? null : ep.id)}
                  className="text-xs text-primary font-medium hover:underline flex items-center gap-1"
                >
                  {showMapping === ep.id ? '▼' : '▶'} Feld-Mappings ({ep.fieldMappings?.length || 0})
                </button>

                {showMapping === ep.id && (
                  <div className="mt-2 space-y-2">
                    {ep.fieldMappings?.map((m) => (
                      <div key={m.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          {ep.direction === 'INBOUND' ? (
                            <>
                              <span className="font-mono text-xs text-gray-500" title="API-Feld (Quelle)">{m.sourceKey || '?'}</span>
                              <span className="text-gray-300">→</span>
                              <span className="font-medium text-gray-700" title="Datenfeld (Ziel)">{m.dataField?.name || '?'}</span>
                              <span className="text-xs text-gray-400">({m.dataField?.type})</span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-gray-700" title="Datenfeld (Quelle)">{m.dataField?.name || '?'}</span>
                              <span className="text-xs text-gray-400">({m.dataField?.type})</span>
                              <span className="text-gray-300">→</span>
                              <span className="font-mono text-xs text-gray-500" title="API-Key (Ziel)">{m.targetKey || m.sourceKey || '?'}</span>
                            </>
                          )}
                          {m.isExport && <span className="text-xs bg-blue-50 text-blue-600 px-1 rounded">Export</span>}
                        </div>
                        <button onClick={() => removeMapping(m.id)} className="text-gray-400 hover:text-red-500"><X size={14} /></button>
                      </div>
                    ))}

                    {/* New Mapping */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 bg-gray-50 rounded px-3 py-2">
                        <select className="text-xs border rounded px-2 py-1 flex-1" value={newMapping.dataFieldId || ''} onChange={(e) => setNewMapping({ ...newMapping, dataFieldId: e.target.value })}>
                          <option value="">— Datenfeld wählen —</option>
                          {fields.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.name} ({f.type}){f.hasPersonReference ? ' 👤' : ''}{f.isSensitive ? ' 🔒' : ''}
                            </option>
                          ))}
                        </select>

                        {ep.direction === 'INBOUND' ? (
                          <>
                            <div className="relative" style={{ flex: 1 }}>
                              {showFieldPicker[ep.id] && previewFields.length > 0 ? (
                                <select
                                  className="text-xs border rounded px-2 py-1 w-full"
                                  value={newMapping.sourceKey || ''}
                                  onChange={(e) => setNewMapping({ ...newMapping, sourceKey: e.target.value })}
                                >
                                  <option value="">— Feld wählen —</option>
                                  {previewFields.map((f) => (
                                    <option key={f} value={f}>{f}</option>
                                  ))}
                                </select>
                              ) : (
                                <input className="text-xs border rounded px-2 py-1 w-full" placeholder="API-Feld-Name (Quelle)" value={newMapping.sourceKey || ''} onChange={(e) => setNewMapping({ ...newMapping, sourceKey: e.target.value })} />
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => fetchApiFields(ep.id)}
                              disabled={previewLoading[ep.id]}
                              className="text-xs px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded whitespace-nowrap disabled:opacity-50"
                              title="Felder von externer API abrufen"
                            >
                              {previewLoading[ep.id] ? '...' : '🔍 Felder'}
                            </button>
                          </>
                        ) : (
                          <input className="text-xs border rounded px-2 py-1 w-full flex-1" placeholder="Ziel-Key (externer Feldname)" value={newMapping.targetKey || ''} onChange={(e) => setNewMapping({ ...newMapping, targetKey: e.target.value })} />
                        )}

                        <input className="text-xs border rounded px-2 py-1 w-24" placeholder={ep.direction === 'INBOUND' ? '(Ziel)' : '(Quelle)'} value={newMapping[ep.direction === 'INBOUND' ? 'targetKey' : 'sourceKey'] || ''} onChange={(e) => setNewMapping({ ...newMapping, [ep.direction === 'INBOUND' ? 'targetKey' : 'sourceKey']: e.target.value })} />
                        <button onClick={() => addMapping(ep.id)} className="btn-primary text-xs px-2 py-1">+</button>
                      </div>

                      {/* Array-Aufteilung: alle Optionen sind standardmaessig aus */}
                      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs text-gray-600">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={newMapping.splitArray === true}
                            onChange={(e) => setNewMapping({ ...newMapping, splitArray: e.target.checked })}
                          />
                          Array aufteilen (ein Wert pro Element)
                        </label>
                        <input
                          className="text-xs border rounded px-2 py-1 flex-1"
                          placeholder="Zeitstempel-Pfad im Element (optional)"
                          value={newMapping.itemTimestampKey || ''}
                          onChange={(e) => setNewMapping({ ...newMapping, itemTimestampKey: e.target.value })}
                        />
                        <label className={`flex items-center gap-1.5 ${newMapping.splitArray === true ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={newMapping.createEntry === true}
                            disabled={newMapping.splitArray !== true}
                            onChange={(e) => setNewMapping({ ...newMapping, createEntry: e.target.checked })}
                          />
                          Eintrag pro Element anlegen
                        </label>
                      </div>

                      {(() => {
                        const selField = fields.find(f => f.id === newMapping.dataFieldId);
                        if (ep.direction === 'OUTBOUND' && selField && (selField.hasPersonReference || selField.isSensitive)) {
                          return (
                            <div className="flex items-start gap-1.5 px-3 py-2 rounded bg-orange-50 border border-orange-200 text-xs text-orange-800">
                              <span className="mt-0.5">⚠️</span>
                              <span>
                                <strong>Datenschutz-Hinweis:</strong> Dieses Feld enthält{' '}
                                {selField.hasPersonReference && selField.isSensitive
                                  ? 'personenbezogene und sensible Daten'
                                  : selField.hasPersonReference
                                    ? 'personenbezogene Daten'
                                    : 'sensible Daten'}
                                . Beim Export an ein externes System sind die Datenschutz-Bestimmungen zu beachten.
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
      ) : null}

      <AiBotWidget
        page="apis"
        active={showForm && !editing}
        onCreated={handleBotCreated}
      />
    </div>
  );
}
